/**
 * ConceptDrawingModal — the "Concept Drawing" tool: a single PDF-backed sketch, opened from a
 * presentation's Sketches wall or a journey Sketch ribbon PDF upload, that the presenter can
 * measure over during a client meeting (Dimension + Area only — no architectural kit; if a
 * presenter wants to actually draw on the sheet, "Send to Sketch" hands it to the full /sketch
 * tool instead). Clients get the same viewer, read-only, with their own ephemeral measuring tools
 * that never save. See docs/CONCEPT_DRAWING.md.
 *
 * Self-contained, QC/Sketch/SiteAnalysis-family chrome — same conventions (dark #07272D top bar,
 * canvas stack, pan/zoom, pdf.js background, commit-based undo/redo) but its own smaller engine;
 * this app has no shared canvas-drawing base to extend (see docs/SITE_ANALYSIS.md).
 *
 * Modes:
 *   'presenter' — Clerk-authed team/presenter access. Full measuring tools; every mutation is a
 *                 saved, persistent `markup` item (the "dimensions layer"); a visibility toggle
 *                 controls whether the client sees this layer at all.
 *   'client'    — public, journey-token-scoped, read-only. Sees the PDF (+ the presenter's
 *                 dimensions layer, only if toggled visible) and can drop Dimension/Area
 *                 measurements of their own — pure local state, never sent anywhere, gone when
 *                 the window closes.
 *
 * World coordinate system = the PDF page's own point space (pdf.js default viewport at scale 1).
 * `scalePixPerFoot` calibrates that space to real feet — set once by the presenter (a hard gate:
 * nothing else in the tool is usable until it's set) so a client is never asked to calibrate.
 *
 * Base-then-sharpen raster (2026-07, matches QcMarkupModal's own pattern): the page renders once
 * at load into a fixed-resolution base raster, drawn at any zoom via canvas transform — fine until
 * zoomed in past its native resolution, where it just looks soft forever. `resharpen()` re-renders
 * the SAME kept-alive pdf.js Page proxy at the settled zoom level (debounced off the wheel handler
 * OR a pinch gesture settling, RESHARPEN_MS) and `render()` prefers that sharper raster once it's
 * ready — the base raster keeps drawing (fast, if a little soft) while a zoom gesture is in progress.
 *
 * Touch (2026-07): there was previously no way to pan or zoom on a touchscreen at all — no wheel
 * event, and empty-space drag on the Select tool only ever cleared the selection (panning required
 * holding Space, a desktop-only concept). `activePointersRef` tracks live touch contacts by
 * pointerId on top of the Pointer Events this file already uses (rather than raw TouchEvents, the
 * `/sketch` tool's approach) — two at once is a pinch (combined pan+zoom, anchored on the previous
 * frame's midpoint, feeding the same resharpen() debounce as a wheel-zoom); one, over empty space
 * with the Select tool active, pans. `touchAction: 'none'` on the event canvas keeps the browser's
 * own native pinch/pan (there's no user-scalable=no on the page) from competing for the gesture.
 *
 * z-index is mode-dependent (2026-07): presenter mode stays at the app's shared 700s "full-screen
 * tool" tier (with QC/Site Analysis/Program Model); client mode bumps to 9500+ to clear the team's
 * Preview overlay (JourneyPreviewOverlay.jsx, zIndex 9400) when opened from inside it — the same
 * convention DrawingSetViewer/PdfFlipViewer/PresentationLink's Overlay already use. Opened from the
 * client's own /journey page (which mounts its own ConceptDrawingHost, no Preview overlay present)
 * either z-index is fine; opened from within Preview, 770 rendered invisibly behind it.
 *
 * Client-mode "Download PDF" reuses the raw bytes already fetched into pdfBytesRef at load (for
 * "Send to Sketch") via a Blob + object URL — forces an actual save rather than opening inline.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { loadPdfjs } from '../lib/pdfjs.js'
import { confirmDialog, promptDialog } from './Dialog.jsx'
import {
  getConceptDrawing, getConceptDrawingForClient, setConceptDrawingScale,
  saveConceptDrawingMarkup, setConceptDrawingMarkupVisible,
} from '../services/conceptDrawings.js'
import { saveSketch } from '../services/sketches.js'

const INK = '#07272D'
const MEASURE = '#1a6fa8'
const HANDLE_R = 6          // screen px
const HIT_TOL = 10          // screen px
const RESHARPEN_MS = 220    // debounce before re-rendering the PDF page at the settled zoom level

/* ── Geometry helpers (all in world units unless noted) ──────────────────────────────────── */
const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)
function distToSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay
  const len2 = dx * dx + dy * dy
  let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}
function pointInPoly(x, y, pts) {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j]
    if (((a.y > y) !== (b.y > y)) && (x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x)) inside = !inside
  }
  return inside
}
function polyArea(pts) {
  let area = 0
  for (let i = 0; i < pts.length; i++) { const j = (i + 1) % pts.length; area += pts[i].x * pts[j].y - pts[j].x * pts[i].y }
  return Math.abs(area) / 2
}
const newId = () => 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 7)

function feetInches(ft) {
  if (!isFinite(ft)) return '—'
  const totalIn = Math.round(ft * 12)
  const f = Math.floor(totalIn / 12), i = totalIn % 12
  return i ? (f + "'-" + i + '"') : (f + "'-0\"")
}
function pxToFeetLabel(px, scale) { return scale ? feetInches(px / scale) : '—' }
function sfLabel(px2, scale) { return scale ? (px2 / (scale * scale)).toFixed(0) + ' SF' : '—' }

/**
 * Parse a real-world distance in feet, accepting feet-and-inches shorthand as well as plain
 * decimal feet: "12", "12.5", "12'", "12'6\"", "12'-6\"", "12' 6\"", "12 6" (space-separated
 * feet + inches), "6\"" (inches only). Returns NaN if nothing usable was typed.
 */
function parseFeetInches(str) {
  const s = String(str || '').trim()
  if (!s) return NaN
  let m = s.match(/^(-?\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)?\s*"?\s*$/)
  if (m) return parseFloat(m[1]) + (m[2] != null ? parseFloat(m[2]) / 12 : 0)
  m = s.match(/^(-?\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)$/)
  if (m) return parseFloat(m[1]) + parseFloat(m[2]) / 12
  m = s.match(/^(\d+(?:\.\d+)?)\s*"\s*$/)
  if (m) return parseFloat(m[1]) / 12
  const dec = parseFloat(s)
  return isFinite(dec) ? dec : NaN
}

const MEASURE_TOOLS = [
  { key: 'dim',  label: 'Dimension' },
  { key: 'area', label: 'Area' },
]

/* ── Draw + hit-test + move, one dispatch per kind (dim + area only — the architectural kit
   was removed; this tool is measure-only now, see docs/CONCEPT_DRAWING.md) ─────────────────── */

/* Dimension line — matches SiteAnalysisModal.jsx's `dimension` kind exactly: ink stroke,
   perpendicular end ticks, no arrowhead, label offset to the side (not centered on the line,
   no background box) — see SITE_STYLES.dimension / labelAnchor / drawInkLabel there. */
function drawDim(g, it, doc, hair) {
  const a = { x: it.ax, y: it.ay }, b = { x: it.bx, y: it.by }
  const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1
  const px = -dy / len, py = dx / len
  const tick = hair(5)
  g.strokeStyle = INK; g.lineWidth = hair(1)
  g.beginPath(); g.moveTo(a.x, a.y); g.lineTo(b.x, b.y); g.stroke()
  for (const p of [a, b]) {
    g.beginPath(); g.moveTo(p.x + px * tick, p.y + py * tick); g.lineTo(p.x - px * tick, p.y - py * tick); g.stroke()
  }
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2
  const offset = hair(11)
  const lx = mx + px * offset, ly = my + py * offset
  const label = doc.scalePixPerFoot ? feetInches(len / doc.scalePixPerFoot) : 'SET SCALE FIRST'
  // Rotate the label to run with the line — normalized to (-90°, 90°] so it never renders upside down.
  let angle = Math.atan2(dy, dx)
  if (angle > Math.PI / 2) angle -= Math.PI
  else if (angle < -Math.PI / 2) angle += Math.PI
  g.save()
  g.translate(lx, ly)
  g.rotate(angle)
  g.font = '600 ' + hair(11) + 'px "DM Sans",sans-serif'
  try { g.letterSpacing = hair(1) + 'px' } catch { /* older engines */ }
  g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'
  g.fillText(label.toUpperCase(), 0, 0)
  g.restore()
}
function drawArea(g, it, doc, hair) {
  if (it.pts.length < 2) return
  g.beginPath()
  it.pts.forEach((p, i) => i ? g.lineTo(p.x, p.y) : g.moveTo(p.x, p.y))
  if (it.pts.length > 2) g.closePath()
  g.fillStyle = 'rgba(26,111,168,0.12)'
  g.strokeStyle = MEASURE
  g.setLineDash([hair(4), hair(3)]); g.lineWidth = hair(1.25)
  if (it.pts.length > 2) g.fill()
  g.stroke(); g.setLineDash([])
  if (it.pts.length > 2) {
    const cx = it.pts.reduce((s, p) => s + p.x, 0) / it.pts.length
    const cy = it.pts.reduce((s, p) => s + p.y, 0) / it.pts.length
    const label = sfLabel(polyArea(it.pts), doc.scalePixPerFoot)
    g.font = hair(11) + 'px "DM Mono",monospace'
    const w = g.measureText(label).width
    g.fillStyle = 'rgba(255,255,255,0.88)'
    g.fillRect(cx - w / 2 - 3, cy - hair(7) - 6, w + 6, hair(14))
    g.fillStyle = MEASURE; g.textAlign = 'center'; g.textBaseline = 'middle'
    g.fillText(label, cx, cy - hair(1))
  }
}
function drawItem(g, it, doc, hair) {
  g.textAlign = 'left'; g.textBaseline = 'alphabetic'; g.setLineDash([])
  if (it.kind === 'dim') drawDim(g, it, doc, hair)
  else if (it.kind === 'area') drawArea(g, it, doc, hair)
}

function hitTest(it, wx, wy, tol) {
  const near = (px, py) => Math.hypot(wx - px, wy - py) < tol
  if (it.kind === 'dim') {
    if (near(it.ax, it.ay)) return 'a'
    if (near(it.bx, it.by)) return 'b'
    if (distToSeg(wx, wy, it.ax, it.ay, it.bx, it.by) < tol) return 'body'
    return null
  }
  if (it.kind === 'area') {
    for (let i = 0; i < it.pts.length; i++) if (near(it.pts[i].x, it.pts[i].y)) return 'v' + i
    if (it.pts.length > 2 && pointInPoly(wx, wy, it.pts)) return 'body'
    return null
  }
  return null
}
function moveItem(it, dx, dy) {
  if (it.kind === 'dim') { it.ax += dx; it.ay += dy; it.bx += dx; it.by += dy }
  else if (it.kind === 'area') it.pts.forEach(p => { p.x += dx; p.y += dy })
}
function itemHandles(it) {
  if (it.kind === 'dim') return [{ x: it.ax, y: it.ay }, { x: it.bx, y: it.by }]
  if (it.kind === 'area') return it.pts
  return []
}

/* ── Host — mounted once; opens on the 'open-concept-drawing-modal' CustomEvent ──────────── */
export default function ConceptDrawingHost() {
  const [launch, setLaunch] = useState(null)
  useEffect(() => {
    const h = e => setLaunch(e.detail || null)
    window.addEventListener('open-concept-drawing-modal', h)
    return () => window.removeEventListener('open-concept-drawing-modal', h)
  }, [])
  if (!launch) return null
  return <ConceptDrawingModal key={launch.sketchId} {...launch} onClose={() => setLaunch(null)} />
}

/**
 * @param {string} sketchId       Concept Sketches record id
 * @param {string} [projectId]    Projects record id (presenter mode — required to create the row)
 * @param {string} [title]        Sketch title, shown in the top bar
 * @param {'presenter'|'client'} [mode]
 * @param {string} [journeyToken] required when mode === 'client'
 * @param {string} [pdfUrl]       a sheet PDF to measure directly, with no concept_drawings row —
 *                                the drawing-set viewer's Measure (docs/SHEET_SCALE.md). Client mode only.
 * @param {number} [pointsPerFoot] its proven scale, PDF points per foot (lib/sheetScale.js)
 */
export function ConceptDrawingModal({ sketchId, projectId, title, mode = 'presenter', journeyToken, pdfUrl: directPdfUrl, pointsPerFoot, onClose }) {
  const isClient = mode === 'client'
  const wrapRef = useRef(null), compRef = useRef(null), uiRef = useRef(null), evRef = useRef(null)
  const vpRef = useRef({ x: 0, y: 0, z: 1 })
  const [, setTick] = useState(0)
  const bump = () => setTick(t => t + 1)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [drawingId, setDrawingId] = useState(null)
  const [scalePixPerFoot, setScalePpf] = useState(null)
  const [markupVisible, setMarkupVisible] = useState(true)
  const [saveStatus, setSaveStatus] = useState('')
  // render() is a plain function re-created every render, but it can be INVOKED from a stale
  // closure — the mount-time load effect calls scheduleRender() once its own async work resolves,
  // and that call site's `render`/`scheduleRender` references were captured at mount, before
  // scalePixPerFoot/markupVisible ever left their initial values. A ref is the same object across
  // every closure, so render() reads these instead of the state directly — otherwise dimensions
  // rendered "SET SCALE FIRST" on load until some other interaction (e.g. a zoom, which calls
  // bump() and forces a fresh closure) happened to re-render with the real values.
  const scalePixPerFootRef = useRef(null)
  const markupVisibleRef = useRef(true)
  function applyScale(v) { scalePixPerFootRef.current = v; setScalePpf(v) }
  function applyMarkupVisible(v) { markupVisibleRef.current = v; setMarkupVisible(v) }
  // Client-only: a local, non-destructive show/hide of ALL dimensions (their own + the
  // presenter's, if visible) — never sent anywhere, purely a decluttering view filter.
  const [dimensionsShown, setDimensionsShown] = useState(true)
  const dimensionsShownRef = useRef(true)
  function applyDimensionsShown(v) { dimensionsShownRef.current = v; setDimensionsShown(v) }
  const pdfPageRef = useRef(null)       // { canvas, w, h } — base raster, rendered once at load, drawn at any zoom
  const pdfPageProxyRef = useRef(null)  // pdf.js Page proxy, kept alive so resharpen() can re-render without re-fetching
  const baseRenderScaleRef = useRef(1)  // the world-scale the base raster was rendered at (see resharpen() below)
  const pdfHiRef = useRef(null)         // { canvas } — sharper raster re-rendered for the current zoom once it settles
  const resharpenTimerRef = useRef(0)
  const itemsRef = useRef([])           // presenter's saved markup (or the visible copy, client mode)
  const clientItemsRef = useRef([])     // client's own ephemeral measurements — never sent anywhere
  const dirtyRef = useRef(false)
  const savingRef = useRef(false)

  const [tool, setTool] = useState('select')
  const toolRef = useRef('select')
  useEffect(() => { toolRef.current = tool }, [tool])
  const [selId, setSelId] = useState(null)
  const selIdRef = useRef(null)
  useEffect(() => { selIdRef.current = selId; scheduleRender() }, [selId]) // eslint-disable-line react-hooks/exhaustive-deps
  const draftRef = useRef(null)   // in-progress area polygon: { kind:'area', pts:[...] }
  const dragRef = useRef(null)
  const [calibrating, setCalibrating] = useState(false)
  const calPtsRef = useRef([])
  const calPointerRef = useRef(null)    // live world-space pointer position while calibrating — the rubber-band line's loose end
  const dimStartRef = useRef(null)      // dim's first point, when armed by a plain click (not a drag) — waiting for a second click to finish
  const hoverPointRef = useRef(null)    // live world-space pointer position while dimStartRef is armed — the rubber-band line's loose end
  const pdfBytesRef = useRef(null)      // raw PDF bytes, kept for "Send to Sketch" (fetched once at load)
  const [sendingToSketch, setSendingToSketch] = useState(false)

  const undoRef = useRef([]), redoRef = useRef([])
  function itemsFor() { return isClient ? clientItemsRef.current : itemsRef.current }
  function snap() { return JSON.stringify(itemsFor()) }
  function saveUndo() {
    undoRef.current.push(snap())
    if (undoRef.current.length > 30) undoRef.current.shift()
    redoRef.current = []
  }
  function undo() {
    if (!undoRef.current.length) return
    redoRef.current.push(snap())
    const prev = undoRef.current.pop()
    if (isClient) clientItemsRef.current = JSON.parse(prev); else itemsRef.current = JSON.parse(prev)
    markDirty(); scheduleRender(); bump()
  }
  function redo() {
    if (!redoRef.current.length) return
    undoRef.current.push(snap())
    const next = redoRef.current.pop()
    if (isClient) clientItemsRef.current = JSON.parse(next); else itemsRef.current = JSON.parse(next)
    markDirty(); scheduleRender(); bump()
  }
  function markDirty() { dirtyRef.current = true }

  /* ── Load ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const drawing = directPdfUrl
          ? { id: null, pdfUrl: directPdfUrl, scalePixPerFoot: pointsPerFoot, markup: [], markupVisible: false }
          : isClient
            ? await getConceptDrawingForClient(journeyToken, sketchId)
            : await getConceptDrawing(sketchId)
        if (cancelled) return
        if (!drawing) { setLoadError('No drawing found for this sketch.'); setLoading(false); return }
        setDrawingId(drawing.id)
        applyScale(drawing.scalePixPerFoot || null)
        applyMarkupVisible(!!drawing.markupVisible)
        itemsRef.current = drawing.markup || []
        const pdfjs = await loadPdfjs()
        if (!pdfjs) throw new Error('PDF viewer could not load')
        const buf = await (await fetch(drawing.pdfUrl)).arrayBuffer()
        // pdf.js transfers `buf` to its worker (structured-clone transfer), which detaches it in
        // this thread — keep our own independent copy for "Send to Sketch" to use later.
        pdfBytesRef.current = buf.slice(0)
        const pdfDoc = await pdfjs.getDocument({ data: buf }).promise
        const page = await pdfDoc.getPage(1)
        pdfPageProxyRef.current = page   // kept alive (never destroyed) so resharpen() can re-render later
        const vp1 = page.getViewport({ scale: 1 })
        const renderScale = Math.min(3, 2400 / vp1.width)
        baseRenderScaleRef.current = renderScale
        const vp = page.getViewport({ scale: renderScale })
        const canvas = document.createElement('canvas')
        canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
        if (cancelled) return
        pdfPageRef.current = { canvas, w: vp1.width, h: vp1.height }
        // Fit to viewport on first paint.
        const wrap = wrapRef.current
        if (wrap) {
          const z = Math.min((wrap.offsetWidth - 48) / vp1.width, (wrap.offsetHeight - 48) / vp1.height, 4)
          vpRef.current = { x: (wrap.offsetWidth - vp1.width * z) / 2, y: (wrap.offsetHeight - vp1.height * z) / 2, z }
        }
        setLoading(false)
        scheduleRender()
      } catch (err) {
        if (!cancelled) { setLoadError(err.message || 'This drawing could not be loaded.'); setLoading(false) }
      }
    })()
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Canvas sizing ────────────────────────────────────────────────────── */
  useEffect(() => {
    function size() {
      const wrap = wrapRef.current
      if (!wrap) return
      for (const ref of [compRef, uiRef, evRef]) if (ref.current) { ref.current.width = wrap.offsetWidth; ref.current.height = wrap.offsetHeight }
      scheduleRender()
    }
    size()
    const ro = new ResizeObserver(size)
    if (wrapRef.current) ro.observe(wrapRef.current)
    return () => ro.disconnect()
  }, [])

  useEffect(() => () => { if (resharpenTimerRef.current) clearTimeout(resharpenTimerRef.current) }, [])

  /* ── Render ───────────────────────────────────────────────────────────── */
  const renderRaf = useRef(0)
  function scheduleRender() {
    if (renderRaf.current) return
    renderRaf.current = requestAnimationFrame(() => { renderRaf.current = 0; render() })
  }
  function render() {
    const comp = compRef.current, ui = uiRef.current
    if (!comp || !ui) return
    const g = comp.getContext('2d'), u = ui.getContext('2d')
    g.setTransform(1, 0, 0, 1, 0, 0); g.clearRect(0, 0, comp.width, comp.height)
    u.setTransform(1, 0, 0, 1, 0, 0); u.clearRect(0, 0, ui.width, ui.height)
    const v = vpRef.current
    const doc = { scalePixPerFoot: scalePixPerFootRef.current }
    const hair = px => px / v.z   // keep stroke widths visually constant regardless of zoom
    g.setTransform(v.z, 0, 0, v.z, v.x, v.y)
    const page = pdfPageRef.current
    // Prefer the sharpened raster resharpen() renders in for the current zoom once it settles
    // (same base-then-sharpen pattern as QcMarkupModal) — drawImage scales whichever source canvas
    // to page.w×page.h regardless of its own native resolution, so swapping is transparent here.
    const hi = pdfHiRef.current
    if (hi) g.drawImage(hi.canvas, 0, 0, page.w, page.h)
    else if (page) g.drawImage(page.canvas, 0, 0, page.w, page.h)
    // Presenter always sees their own layer while editing. Client sees the presenter's layer only
    // if the presenter made it visible AND their own local "show dimensions" filter is on; their
    // own added measurements respect that same local filter.
    if (!isClient) {
      itemsRef.current.forEach(it => drawItem(g, it, doc, hair))
    } else {
      if (markupVisibleRef.current && dimensionsShownRef.current) itemsRef.current.forEach(it => drawItem(g, it, doc, hair))
      if (dimensionsShownRef.current) clientItemsRef.current.forEach(it => drawItem(g, it, doc, hair))
    }
    g.setTransform(1, 0, 0, 1, 0, 0)

    // UI overlay: selection handles + in-progress drafts, in the same world transform
    u.setTransform(v.z, 0, 0, v.z, v.x, v.y)
    const sel = (!isClient || dimensionsShownRef.current) ? itemsFor().find(i => i.id === selIdRef.current) : null
    if (sel) {
      u.strokeStyle = MEASURE
      u.lineWidth = hair(1.25); u.setLineDash([hair(4), hair(3)])
      u.setLineDash([])
      itemHandles(sel).forEach(h => {
        u.beginPath(); u.arc(h.x, h.y, HANDLE_R / v.z, 0, Math.PI * 2)
        u.fillStyle = '#fff'; u.fill(); u.strokeStyle = MEASURE; u.lineWidth = hair(1.5); u.stroke()
      })
    }
    const draft = draftRef.current
    if (draft) {
      if (draft.kind === 'area' && draft.pts.length) {
        u.strokeStyle = MEASURE; u.lineWidth = hair(1.25); u.setLineDash([hair(4), hair(3)])
        u.beginPath()
        draft.pts.forEach((p, i) => i ? u.lineTo(p.x, p.y) : u.moveTo(p.x, p.y))
        u.stroke(); u.setLineDash([])
        draft.pts.forEach(p => { u.beginPath(); u.arc(p.x, p.y, HANDLE_R / v.z, 0, Math.PI * 2); u.fillStyle = MEASURE; u.fill() })
      }
    }
    // Calibration: show the clicked point(s) + a live rubber-band line to the pointer before the
    // second click lands — previously nothing was drawn here at all, so calibrating felt like
    // clicking blind.
    if (calibrating) {
      const pts = calPtsRef.current
      u.fillStyle = INK
      pts.forEach(p => { u.beginPath(); u.arc(p.x, p.y, HANDLE_R / v.z, 0, Math.PI * 2); u.fill() })
      if (pts.length === 1 && calPointerRef.current) {
        u.strokeStyle = INK; u.lineWidth = hair(1.25); u.setLineDash([hair(5), hair(4)])
        u.beginPath(); u.moveTo(pts[0].x, pts[0].y); u.lineTo(calPointerRef.current.x, calPointerRef.current.y); u.stroke()
        u.setLineDash([])
      }
    }
    // Dimension: an in-progress drag shows a live rubber-band line the same way an armed
    // click-to-click point does (below) — previously a drag placement had no live feedback at all.
    if (dragRef.current && dragRef.current.placing === 'dim') {
      const { start: a2, last: b2 } = dragRef.current
      u.strokeStyle = INK; u.lineWidth = hair(1); u.setLineDash([hair(5), hair(4)])
      u.beginPath(); u.moveTo(a2.x, a2.y); u.lineTo(b2.x, b2.y); u.stroke()
      u.setLineDash([])
    }
    // Dimension: the first point was placed with a plain click (not a drag) — show it plus a live
    // rubber-band line to the pointer, waiting for the second click to finish.
    if (dimStartRef.current) {
      u.fillStyle = INK
      u.beginPath(); u.arc(dimStartRef.current.x, dimStartRef.current.y, HANDLE_R / v.z, 0, Math.PI * 2); u.fill()
      if (hoverPointRef.current) {
        u.strokeStyle = INK; u.lineWidth = hair(1); u.setLineDash([hair(5), hair(4)])
        u.beginPath(); u.moveTo(dimStartRef.current.x, dimStartRef.current.y); u.lineTo(hoverPointRef.current.x, hoverPointRef.current.y); u.stroke()
        u.setLineDash([])
      }
    }
    // Area: dragging from a blank start shows a live rectangle preview — the fast alternative to
    // click-click-click-click for a rectangular room.
    if (dragRef.current && dragRef.current.placing === 'area-rect') {
      const { start: a3, last: b3 } = dragRef.current
      u.strokeStyle = MEASURE; u.lineWidth = hair(1.25); u.setLineDash([hair(4), hair(3)])
      u.strokeRect(Math.min(a3.x, b3.x), Math.min(a3.y, b3.y), Math.abs(b3.x - a3.x), Math.abs(b3.y - a3.y))
      u.setLineDash([])
    }
    u.setTransform(1, 0, 0, 1, 0, 0)
  }
  useEffect(scheduleRender, [scalePixPerFoot, markupVisible, dimensionsShown, drawingId])

  /* ── Coordinate helpers ───────────────────────────────────────────────── */
  function toWorld(e) {
    const rect = evRef.current.getBoundingClientRect()
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top
    const v = vpRef.current
    return { x: (sx - v.x) / v.z, y: (sy - v.y) / v.z }
  }

  /* ── Pointer handlers ─────────────────────────────────────────────────── */
  const spaceRef = useRef(false)
  const panRef = useRef(null)
  // Touch (2026-07): there was no pan or zoom path for a touchscreen at all before this — no wheel
  // event to zoom with, and empty-space drag only ever cleared the selection instead of panning
  // (that required holding Space, a desktop-only concept). activePointersRef tracks every live
  // touch contact by pointerId; two at once means a pinch, handled the same combined pan+zoom way
  // as `/sketch`'s native touch handlers, but built on the Pointer Events this file already uses
  // instead of raw TouchEvents, and reusing resharpen() so a pinch benefits from the same
  // base-then-sharpen upgrade a mouse-wheel zoom gets.
  const activePointersRef = useRef(new Map())
  const pinchLastRef = useRef(null)     // { dist, x, y } from the previous pinch move, for incremental zoom+pan
  const touchPanRef = useRef(null)      // single-finger pan (Select tool, empty space) — { x, y, vx, vy }
  function onPointerDown(e) {
    if (loading || loadError) return
    if (e.pointerType === 'touch') {
      activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (activePointersRef.current.size === 2) {
        // A second finger just landed — this is a pinch, full stop. Cancel anything a first
        // finger may have started (a pan, or a draw-tool drag) so they don't fight the pinch.
        touchPanRef.current = null
        dragRef.current = null
        const pts = [...activePointersRef.current.values()]
        pinchLastRef.current = { dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 }
        return
      }
      if (activePointersRef.current.size > 2) return   // a third finger — ignore, still mid-pinch
    }
    // Middle-click (or space+click) always pans — checked FIRST so it can never register as a
    // calibration point or a tool click (previously calibrating was checked first, so a
    // middle-click while calibrating landed a real point instead of panning).
    if (spaceRef.current || e.button === 1) { panRef.current = { x: e.clientX, y: e.clientY, vx: vpRef.current.x, vy: vpRef.current.y }; return }
    if (calibrating) {
      const w = toWorld(e)
      calPtsRef.current.push(w)
      if (calPtsRef.current.length === 2) finishCalibration()
      else scheduleRender()
      return
    }
    if (isClient && scalePixPerFoot == null) return // client can't do anything meaningful before scale exists
    const w = toWorld(e)
    const t = toolRef.current
    const tol = HIT_TOL / vpRef.current.z

    if (t === 'select') {
      const list = itemsFor()
      for (let i = list.length - 1; i >= 0; i--) {
        const hit = hitTest(list[i], w.x, w.y, tol)
        if (hit) {
          setSelId(list[i].id)
          saveUndo()
          dragRef.current = { id: list[i].id, hit, last: w, changed: false }
          return
        }
      }
      // No item hit — on a touchscreen this IS the pan gesture (there's no modifier key to hold
      // like desktop's Space+drag), so a single finger over empty space pans instead of just
      // clearing the selection.
      if (e.pointerType === 'touch') {
        touchPanRef.current = { x: e.clientX, y: e.clientY, vx: vpRef.current.x, vy: vpRef.current.y }
      }
      setSelId(null); scheduleRender()
      return
    }
    if (t === 'area') {
      // Click on the first placed vertex closes the polygon (needs ≥3 points down already).
      if (draftRef.current && draftRef.current.pts.length >= 3) {
        const p0 = draftRef.current.pts[0]
        if (Math.hypot(w.x - p0.x, w.y - p0.y) < tol) { finishArea(); return }
      }
      if (!draftRef.current) {
        // First interaction with a fresh area — could become a click-built polygon (a plain
        // click) or a drag-to-rectangle shortcut (four corners, already closed); decided on
        // release in onPointerUp.
        dragRef.current = { placing: 'area-rect', start: w, last: w }
        scheduleRender()
        return
      }
      draftRef.current.pts.push(w)
      scheduleRender()
      return
    }
    if (t === 'dim') {
      if (dimStartRef.current) {
        // Second click — finish the dimension armed by the previous plain click.
        const a = dimStartRef.current, b = w
        dimStartRef.current = null; hoverPointRef.current = null
        if (dist(a, b) > 2 / vpRef.current.z) {
          const it = { id: newId(), kind: 'dim', ax: a.x, ay: a.y, bx: b.x, by: b.y }
          saveUndo(); itemsFor().push(it); markDirty(); setSelId(it.id)
        }
        setTool('select'); scheduleRender(); bump()
        return
      }
      // First interaction — could become a drag (finish on release) or a plain click (arm this
      // point and wait for a second click); decided on release in onPointerUp.
      dragRef.current = { placing: 'dim', start: w, last: w }
      scheduleRender()
      return
    }
  }
  function onPointerMove(e) {
    if (e.pointerType === 'touch' && activePointersRef.current.has(e.pointerId)) {
      activePointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    }
    if (activePointersRef.current.size === 2 && pinchLastRef.current) {
      const pts = [...activePointersRef.current.values()]
      const dist2 = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y)
      const midx = (pts[0].x + pts[1].x) / 2, midy = (pts[0].y + pts[1].y) / 2
      const last = pinchLastRef.current
      const rect = evRef.current.getBoundingClientRect()
      const px = last.x - rect.left, py = last.y - rect.top   // previous frame's pinch midpoint, canvas-local
      const cx = midx - rect.left, cy = midy - rect.top       // this frame's pinch midpoint, canvas-local
      const v = vpRef.current
      const nz = Math.min(6, Math.max(0.15, v.z * (dist2 / (last.dist || dist2 || 1))))
      const f = nz / v.z   // the clamp-adjusted scale actually applied this frame
      // Combines pan (midpoint moved from the previous frame's to this one's) and zoom (scale
      // changed by f) in a single step, anchored on the PREVIOUS frame's reference point — the
      // same math as onWheel's cursor-anchored zoom, just re-derived every move instead of once
      // per discrete wheel tick, since a pinch's midpoint drifts continuously as fingers move.
      v.x = cx - (px - v.x) * f
      v.y = cy - (py - v.y) * f
      v.z = nz
      pinchLastRef.current = { dist: dist2, x: midx, y: midy }
      pdfHiRef.current = null
      scheduleRender(); bump()
      if (resharpenTimerRef.current) clearTimeout(resharpenTimerRef.current)
      resharpenTimerRef.current = setTimeout(resharpen, RESHARPEN_MS)
      return
    }
    if (touchPanRef.current) {
      const p = touchPanRef.current
      vpRef.current.x = p.vx + (e.clientX - p.x); vpRef.current.y = p.vy + (e.clientY - p.y)
      scheduleRender(); return
    }
    if (panRef.current) {
      const p = panRef.current
      vpRef.current.x = p.vx + (e.clientX - p.x); vpRef.current.y = p.vy + (e.clientY - p.y)
      scheduleRender(); return
    }
    if (calibrating) { calPointerRef.current = toWorld(e); scheduleRender(); return }
    if (dimStartRef.current && !dragRef.current) { hoverPointRef.current = toWorld(e); scheduleRender(); return }
    const drag = dragRef.current
    if (!drag) return
    const w = toWorld(e)
    if (drag.placing) { drag.last = w; scheduleRender(); return }
    const list = itemsFor()
    const it = list.find(i => i.id === drag.id)
    if (!it) return
    const dx = w.x - drag.last.x, dy = w.y - drag.last.y
    applyDrag(it, drag.hit, w, dx, dy)
    drag.last = w; drag.changed = true
    scheduleRender()
  }
  function applyDrag(it, hit, w, dx, dy) {
    if (hit === 'body') { moveItem(it, dx, dy); return }
    if (it.kind === 'dim') {
      if (hit === 'a') { it.ax = w.x; it.ay = w.y } else if (hit === 'b') { it.bx = w.x; it.by = w.y }
      return
    }
    if (it.kind === 'area' && hit && hit[0] === 'v') { const idx = parseInt(hit.slice(1), 10); it.pts[idx] = w }
  }
  function onPointerUp(e) {
    if (e && e.pointerType === 'touch') {
      activePointersRef.current.delete(e.pointerId)
      if (activePointersRef.current.size < 2) pinchLastRef.current = null
    }
    if (touchPanRef.current) { touchPanRef.current = null; return }
    if (panRef.current) { panRef.current = null; return }
    const drag = dragRef.current
    if (drag) {
      if (drag.placing === 'dim') {
        const a = drag.start, b = drag.last
        if (dist(a, b) > 4 / vpRef.current.z) {
          // Moved enough to count as a drag — commit immediately (classic click-drag-release).
          const it = { id: newId(), kind: 'dim', ax: a.x, ay: a.y, bx: b.x, by: b.y }
          saveUndo(); itemsFor().push(it); markDirty(); setSelId(it.id)
          setTool('select')
        } else {
          // A plain click, not a drag — arm the first point and wait for a second click to
          // finish (stay in the dim tool; nothing was created yet).
          dimStartRef.current = a
          hoverPointRef.current = a
        }
      } else if (drag.placing === 'area-rect') {
        const a = drag.start, b = drag.last
        if (dist(a, b) > 4 / vpRef.current.z) {
          // Dragged — commit a closed, axis-aligned four-corner rectangle.
          const pts = [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }]
          const it = { id: newId(), kind: 'area', pts }
          saveUndo(); itemsFor().push(it); markDirty(); setSelId(it.id)
          setTool('select')
        } else {
          // A plain click, not a drag — start the click-built polygon with this vertex.
          draftRef.current = { kind: 'area', pts: [a] }
        }
      } else if (drag.changed) {
        markDirty()
      } else {
        // no-op drag — pop the undo entry pushed on pointerdown, it never actually changed anything
        undoRef.current.pop()
      }
      dragRef.current = null
      scheduleRender(); bump()
    }
  }
  // A double-click finishes the in-progress area polygon (the second click of the double-click
  // already added a near-duplicate vertex at this same spot — drop it before closing).
  function onDoubleClick() {
    if (toolRef.current !== 'area' || !draftRef.current) return
    if (draftRef.current.pts.length >= 2) draftRef.current.pts.pop()
    if (draftRef.current.pts.length >= 3) finishArea()
    else scheduleRender()
  }
  function onWheel(e) {
    e.preventDefault()
    const rect = evRef.current.getBoundingClientRect()
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top
    const f = e.deltaY < 0 ? 1.1 : 1 / 1.1
    const v = vpRef.current
    const nz = Math.min(6, Math.max(0.15, v.z * f))
    const zr = nz / v.z
    v.x = cx - zr * (cx - v.x); v.y = cy - zr * (cy - v.y); v.z = nz
    // Drop the previous zoom level's sharpened raster immediately (the base raster still draws
    // fine meanwhile, just softer past its native resolution) and debounce a fresh one for the
    // NEW zoom — same base-raster-while-moving, resharpen-once-it-settles pattern as QcMarkupModal.
    pdfHiRef.current = null
    scheduleRender(); bump()
    if (resharpenTimerRef.current) clearTimeout(resharpenTimerRef.current)
    resharpenTimerRef.current = setTimeout(resharpen, RESHARPEN_MS)
  }
  // Re-renders the PDF page at the current zoom level once it settles, so the raster is crisp at
  // any magnification instead of forever scaling the one bitmap rendered at load. Only bothers
  // once zoomed in PAST the base raster's own native resolution — zoomed out, the base raster is
  // already sharper than the screen needs, so re-rendering smaller would only waste work.
  async function resharpen() {
    const page = pdfPageProxyRef.current
    if (!page) return
    const z = vpRef.current.z
    if (z <= baseRenderScaleRef.current * 1.05) { pdfHiRef.current = null; scheduleRender(); return }
    try {
      const vp = page.getViewport({ scale: Math.min(8, z) })
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(vp.width); canvas.height = Math.ceil(vp.height)
      await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise
      pdfHiRef.current = { canvas }
    } catch { pdfHiRef.current = null }
    scheduleRender()
  }
  function finishArea() {
    const draft = draftRef.current
    if (draft && draft.pts.length >= 3) { saveUndo(); itemsFor().push({ id: newId(), kind: 'area', pts: draft.pts }); markDirty() }
    draftRef.current = null
    setTool('select'); scheduleRender(); bump()
  }
  // Leave whatever tool is active and return to Select — finishes an in-progress area (if it has
  // enough points to be worth keeping) and drops an armed-but-unfinished dimension click.
  function leaveTool() {
    if (tool === 'area' && draftRef.current) {
      if (draftRef.current.pts.length >= 3) finishArea()
      else { draftRef.current = null; scheduleRender() }
    }
    if (dimStartRef.current) { dimStartRef.current = null; hoverPointRef.current = null }
    setTool('select')
  }

  useEffect(() => {
    function kd(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      if (e.key === ' ') { e.preventDefault(); spaceRef.current = true }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) { e.preventDefault(); if (e.shiftKey) redo(); else undo() }
      if (e.key === 'Enter' && draftRef.current) { e.preventDefault(); finishArea() }
      if (e.key === 'Escape') {
        if (draftRef.current) { draftRef.current = null; scheduleRender() }
        else if (dimStartRef.current) { dimStartRef.current = null; hoverPointRef.current = null; scheduleRender() }
        else if (calibrating) { setCalibrating(false); calPtsRef.current = []; calPointerRef.current = null }
        else setSelId(null)
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selIdRef.current) {
        saveUndo()
        if (isClient) clientItemsRef.current = clientItemsRef.current.filter(i => i.id !== selIdRef.current)
        else itemsRef.current = itemsRef.current.filter(i => i.id !== selIdRef.current)
        setSelId(null); if (!isClient) markDirty(); scheduleRender(); bump()
      }
    }
    function ku(e) { if (e.key === ' ') spaceRef.current = false }
    window.addEventListener('keydown', kd)
    window.addEventListener('keyup', ku)
    return () => { window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku) }
  }, [calibrating]) // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Scale calibration (presenter only) ───────────────────────────────── */
  async function finishCalibration() {
    const [a, b] = calPtsRef.current
    const px = dist(a, b)
    calPtsRef.current = []
    calPointerRef.current = null
    setCalibrating(false)
    const ans = await promptDialog({
      title: 'Set scale',
      message: "Real-world distance between the two points — feet and inches (e.g. 12'-6\", or plain 12.5):",
      placeholder: "12'-6\"",
    })
    if (ans === null) return
    const feet = parseFeetInches(ans)
    if (!(feet > 0)) return
    const ppf = px / feet
    applyScale(ppf)
    setSaveStatus('Saving…')
    try { await setConceptDrawingScale(drawingId, ppf); setSaveStatus('Scale set') } catch { setSaveStatus('Could not save scale') }
    scheduleRender()
  }

  /* ── Download (client only — presenter has direct file access via other tools) ──────────────
     Reuses the same raw bytes already fetched into pdfBytesRef at load time (for "Send to Sketch")
     instead of a second network round-trip — a Blob + object URL forces an actual save-to-device
     rather than just opening inline in the browser, which is what a plain link to the Supabase
     URL would otherwise do. */
  function handleDownloadPdf() {
    const bytes = pdfBytesRef.current
    if (!bytes) return
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = (title || 'Drawing').replace(/[\\/:*?"<>|]+/g, '').trim() + '.pdf'
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 4000)
  }

  /* ── Send to Sketch (presenter only) ────────────────────────────────────
     Hands the calibrated PDF off to the full /sketch tool as a new, already-scaled document —
     one image layer embedding the same PDF bytes, sized to the PDF's own point space (the exact
     world-unit basis ConceptDrawingModal already uses), so scalePixPerFoot carries over unchanged. */
  function u8ToDataUrl(u8) {
    let s = ''
    for (let i = 0; i < u8.length; i += 0x8000) s += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000))
    return 'data:application/pdf;base64,' + btoa(s)
  }
  async function handleSendToSketch() {
    const page = pdfPageRef.current, bytes = pdfBytesRef.current
    if (!page || !bytes || sendingToSketch) return
    setSendingToSketch(true)
    try {
      const name = title || 'Concept Drawing'
      // A modest assumed viewport so the page opens roughly fit-to-view instead of at 100%.
      const fitZ = Math.min(1, 1200 / page.w, 800 / page.h)
      const doc = {
        version: 2,
        scalePixPerFoot, scaleUnit: 'imperial',
        vp: { x: 40, y: 40, z: fitZ },
        layers: [{
          name, type: 'image', visible: true, x: 0, y: 0, w: page.w, h: page.h, rotation: 0,
          paperOpacity: 1, inkOpacity: 1, paperColor: 'buff', aspectRatio: page.w / page.h,
          pdfData: u8ToDataUrl(new Uint8Array(bytes)),
          png: page.canvas.toDataURL('image/png'),
        }],
        measurements: [], textObjects: [],
      }
      const jsonBlob = new Blob([JSON.stringify(doc)], { type: 'application/json' })
      const thumbBlob = await new Promise(res => {
        const c = document.createElement('canvas')
        const tw = 320, th = Math.max(1, Math.round(page.canvas.height * (tw / page.canvas.width)))
        c.width = tw; c.height = th
        c.getContext('2d').drawImage(page.canvas, 0, 0, tw, th)
        c.toBlob(res, 'image/png')
      })
      const newId2 = await saveSketch(null, name, jsonBlob, thumbBlob)
      window.open('/sketch?id=' + newId2, '_blank', 'noopener')
    } catch (err) {
      setSaveStatus('Send to AlderSKETCH failed: ' + (err.message || 'unknown error'))
    } finally {
      setSendingToSketch(false)
    }
  }

  /* ── Save (presenter markup) — debounced on mutation + flushed on close ─ */
  const saveTimer = useRef(0)
  useEffect(() => {
    if (isClient) return
    const iv = setInterval(async () => {
      if (dirtyRef.current && !savingRef.current && drawingId) {
        savingRef.current = true; dirtyRef.current = false
        setSaveStatus('Saving…')
        try { await saveConceptDrawingMarkup(drawingId, itemsRef.current); setSaveStatus('Saved') }
        catch { setSaveStatus('Save failed'); dirtyRef.current = true }
        savingRef.current = false
      }
    }, 4000)
    return () => clearInterval(iv)
  }, [isClient, drawingId])

  async function handleClose() {
    if (!isClient && dirtyRef.current && drawingId) {
      try { await saveConceptDrawingMarkup(drawingId, itemsRef.current) } catch { /* best-effort */ }
    }
    onClose()
  }
  async function toggleMarkupVisible() {
    const next = !markupVisible
    applyMarkupVisible(next)
    try { await setConceptDrawingMarkupVisible(drawingId, next) } catch { applyMarkupVisible(!next) }
  }
  async function clearMeasurements() {
    if (!(await confirmDialog({ title: 'Delete your measurements?', message: 'Permanently remove the measurements you’ve added? This cannot be undone.', confirmLabel: 'Delete', danger: true }))) return
    clientItemsRef.current = []
    setSelId(null); scheduleRender(); bump()
  }

  /* ── UI ───────────────────────────────────────────────────────────────── */
  const chip = { fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '5px 11px', borderRadius: 4, border: '0.5px solid rgba(255,255,255,0.28)', background: 'transparent', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', whiteSpace: 'nowrap' }
  const chipActive = { ...chip, background: '#fff', color: INK, fontWeight: 600, border: 'none' }
  const sel = itemsFor().find(i => i.id === selId)

  return createPortal(
    // Client mode is only ever reached from within the journey — either the client's own /journey
    // page (which mounts its own ConceptDrawingHost with nothing else layered above) or the team's
    // Preview overlay (JourneyPreviewOverlay.jsx, zIndex 9400, a SIBLING portal to document.body).
    // Presenter mode stays at 770 with the app's other full-screen tools (QC/Site Analysis/Program
    // Model), which is correct there since it's opened directly from the outline with no journey
    // overlay in the way — but at 770 a client-mode open from inside Preview rendered invisibly
    // BEHIND it (only visible after closing Preview). 9500+ matches the convention every other
    // in-journey viewer already uses to clear it (DrawingSetViewer/PdfFlipViewer/PresentationLink).
    <div data-modal-id="ConceptDrawingModal" style={{ position: 'fixed', inset: 0, zIndex: directPdfUrl ? 9650 : (isClient ? 9500 : 770),   /* 9650: above the drawing-set viewer it opens from */ background: '#e9edee', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', background: INK, color: '#fff', flexShrink: 0, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 600, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {title || 'Concept Drawing'}
        </span>
        {!isClient && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>{saveStatus}</span>}
        <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.15)', margin: '0 2px' }} />
        <button style={tool === 'select' ? chipActive : chip} onClick={leaveTool}>Select</button>
        {MEASURE_TOOLS.map(mt => (
          <button key={mt.key} style={tool === mt.key ? chipActive : chip}
            onClick={() => {
              if (tool === 'area' && draftRef.current) finishArea()
              if (dimStartRef.current) { dimStartRef.current = null; hoverPointRef.current = null }
              setTool(mt.key)
            }}>{mt.label}</button>
        ))}
        {tool === 'area' && draftRef.current && draftRef.current.pts.length >= 3 && (
          <button style={chipActive} onClick={finishArea}>Close shape</button>
        )}
        {!isClient && <button style={chip} onClick={() => {
          leaveTool()
          setCalibrating(true); calPtsRef.current = []; calPointerRef.current = null
        }}>
          {scalePixPerFoot ? 'Recalibrate' : 'Set Scale'}
        </button>}
        {!isClient && (
          <button style={chip} onClick={toggleMarkupVisible} title="Whether the client sees the dimensions layer">
            {markupVisible ? '● Dimensions: visible to client' : '○ Dimensions: hidden from client'}
          </button>
        )}
        {!isClient && scalePixPerFoot != null && (
          <button style={chip} onClick={handleSendToSketch} disabled={sendingToSketch} title="Open this drawing, already scaled, in the full /sketch tool">
            {sendingToSketch ? 'Sending…' : 'Send to AlderSKETCH'}
          </button>
        )}
        {isClient && (
          <button style={chip} onClick={() => applyDimensionsShown(!dimensionsShown)}>
            {dimensionsShown ? 'Hide Dimensions' : 'Show Dimensions'}
          </button>
        )}
        {isClient && <button style={chip} onClick={handleDownloadPdf} title="Download this drawing as a PDF">Download PDF</button>}
        {isClient && clientItemsRef.current.length > 0 && <button style={{ ...chip, color: '#f0a392' }} onClick={clearMeasurements}>Delete my measurements</button>}
        <div style={{ flex: 1 }} />
        <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{Math.round(vpRef.current.z * 100)}%</span>
        <button style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 15, cursor: 'pointer', padding: '2px 6px' }} title="Close" onClick={handleClose}>✕</button>
      </div>

      {isClient && (
        <div style={{ padding: '6px 14px', background: '#fff', borderBottom: '0.5px solid var(--aj-border)', fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)' }}>
          Pick Dimension or Area above to measure — your marks clear when you close this window.
          {directPdfUrl && ' Measurements are approximate; the written dimensions on the drawings govern.'}
        </div>
      )}

      <div ref={wrapRef} style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>
            Loading drawing…
          </div>
        )}
        {loadError && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#b8540a' }}>
            {loadError}
          </div>
        )}
        <canvas ref={compRef} style={{ position: 'absolute', inset: 0 }} />
        <canvas ref={uiRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        <canvas ref={evRef}
          // touchAction: 'none' — without it the browser's own native pinch/pan takes the gesture
          // instead of the custom touch handling above (the page has no user-scalable=no either,
          // so a two-finger pinch here would otherwise zoom the whole viewport, not the drawing).
          style={{ position: 'absolute', inset: 0, touchAction: 'none', cursor: calibrating ? 'crosshair' : (tool === 'select' ? 'default' : 'crosshair') }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp} onPointerCancel={onPointerUp} onWheel={onWheel}
          onDoubleClick={onDoubleClick}
        />
        {!isClient && !loading && !loadError && scalePixPerFoot == null && !calibrating && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(7,39,45,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', maxWidth: 340, textAlign: 'center' }}>
              <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.04em', color: INK, marginBottom: 8 }}>Set the scale first</div>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', lineHeight: 1.5, marginBottom: 14 }}>
                Click two points a known distance apart on the drawing so the measuring tools read in real feet — clients will never be asked to do this.
              </div>
              <button style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, padding: '8px 16px', borderRadius: 4, border: 'none', background: INK, color: '#fff', cursor: 'pointer' }}
                onClick={() => {
                  setCalibrating(true); calPtsRef.current = []; calPointerRef.current = null
                  dimStartRef.current = null; hoverPointRef.current = null
                }}>Set Scale</button>
            </div>
          </div>
        )}
        {calibrating && (
          <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: INK, color: '#fff', padding: '6px 14px', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>
            Click two points a known distance apart — {calPtsRef.current.length}/2
          </div>
        )}
        {!calibrating && tool === 'dim' && dimStartRef.current && (
          <div style={{ position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', background: INK, color: '#fff', padding: '6px 14px', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 12 }}>
            Click the other end to finish — Esc to cancel
          </div>
        )}
      </div>

      {!isClient && sel && (
        <div style={{ position: 'absolute', right: 14, top: 60, width: 220, background: 'var(--aj-bg-surface)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, padding: 12, boxShadow: '0 4px 16px rgba(7,39,45,0.15)' }}>
          <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 8 }}>{sel.kind}</div>
          {sel.kind === 'dim' && (
            <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' }}>
              {pxToFeetLabel(dist({ x: sel.ax, y: sel.ay }, { x: sel.bx, y: sel.by }), scalePixPerFoot)}
            </div>
          )}
          {sel.kind === 'area' && sel.pts.length > 2 && (
            <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' }}>
              {sfLabel(polyArea(sel.pts), scalePixPerFoot)}
            </div>
          )}
          <button style={{ marginTop: 8, ...chip, color: '#b8272c', border: '0.5px solid rgba(184,39,44,0.35)' }}
            onClick={() => { saveUndo(); itemsRef.current = itemsRef.current.filter(i => i.id !== sel.id); setSelId(null); markDirty(); scheduleRender(); bump() }}>Delete</button>
        </div>
      )}
    </div>,
    document.body
  )
}
